// Fiók létrehozása (full-page, create-mód) — a /clients/new mintájára. A form-INSTANCE itt él (a 422 → mező-hiba
// a mutációból írható). Idempotency-Key a create-en (form-instance-enként stabil, a dupla-POST ellen). A szülő-
// ügyfél a form fölött álló EntityCombobox-ból jön (a `clientId` NEM a form része — a create-body-hoz külön adjuk);
// `?clientId=` search-param esetén előtöltve + zárolva (az ügyfél-detail „Fiók hozzáadása" belépő). A számlázási-
// profil választó opcióit a kiválasztott szülő-ügyfél detail-je (`clients.get` → billingProfiles) adja, best-effort.
// Siker → a friss fiók detail-oldalára (ami egyben a szerkesztő). Page-gate: accounts.manage.
//
// A LAP FELÉPÍTÉSE a szerkesztő-oldal tükre (`accounts.$id.tsx`): egy balra igazított `FormPage`-hasáb,
// `FormSection`-ök ugyanabban a sorrendben — a három kapcsolódó szekció (Szolgáltatások · Kapcsolattartók ·
// Ügyféladminisztrátorok) ZÁROLVA már itt is látszik (`LockedSection`). Miért látszik: a felhasználó így
// létrehozáskor tanulja meg a fiók teljes hatókörét, és a mentés után UGYANOTT találja meg őket. Ragadós
// szekció-nav itt SZÁNDÉKOSAN nincs (a `clients.new` mintája): a lap rövid, a rail csak zajt tenne rá.
import { AccountCreateResponseSchema } from '@atlas/contracts';
import { IdentificationIcon, Square3Stack3DIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/shared/app-shell';
import { EmptyState } from '@/components/shared/empty-state';
import { type ComboboxOption } from '@/components/shared/entity-combobox';
import { FormPage } from '@/components/shared/form-page';
import { LockedSection } from '@/components/shared/locked-section';
import { Button } from '@/components/ui/button';
import { accountKeys } from '@/features/accounts/api';
import { AccountForm } from '@/features/accounts/components/account-form';
import {
  ACCOUNT_SELECT_NONE,
  EMPTY_ACCOUNT_FORM,
  accountFormSchemaWithRules,
  accountFormToCreateBody,
  buildAccountErrorMap,
  type AccountFormValues,
} from '@/features/accounts/form';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { newIdempotencyKey, useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

export const Route = createFileRoute('/accounts/new')({
  // A szülő-ügyfél előtöltése: `?clientId=` (az ügyfél-detail „Fiók hozzáadása" belépő) → zárolt választó.
  validateSearch: (search: Record<string, unknown>): { clientId?: string } =>
    typeof search.clientId === 'string' ? { clientId: search.clientId } : {},
  beforeLoad: () => requireAuth(),
  component: AccountCreatePage,
});

function AccountCreatePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can, isLoading: permsLoading } = usePermissions();
  const { clientId: presetClientId } = Route.useSearch();

  // A kiválasztott szülő-ügyfél (init: a search-param; ha van, a választó zárolt). A `clientId` NEM a form része.
  const [clientId, setClientId] = useState<string | null>(presetClientId ?? null);
  const locked = Boolean(presetClientId);

  // Form-instance-enként stabil idempotency-kulcs (retry-nál is ugyanaz → nincs dupla fiók).
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const form = useForm<AccountFormValues>({
    resolver: zodResolver(accountFormSchemaWithRules, {
      errorMap: buildAccountErrorMap(t),
    }) as Resolver<AccountFormValues>,
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_ACCOUNT_FORM,
  });

  const refsQ = api.accountRefs.useQuery(['account-refs'], {});
  const refs = refsQ.data?.status === 200 ? refsQ.data.body : undefined;

  // A szülő-ügyfél detail-je (a számlázási profilok forrása + a zárolt választó címkéje). Csak ha van clientId.
  const parentKey = ['clients', 'detail', clientId ?? ''];
  const parentQ = api.clients.get.useQuery(
    parentKey,
    { params: { id: clientId ?? '' } },
    { queryKey: parentKey, enabled: clientId !== null },
  );
  const parent = parentQ.data?.status === 200 ? parentQ.data.body : undefined;
  const billingProfileOptions = parent?.billingProfiles ?? [];
  // A zárolt (search-param) választó címkéje a detailből (hogy ne csak a nyers id látsszon).
  const clientInitialOption: ComboboxOption | null = useMemo(
    () => (clientId && parent ? { id: clientId, label: parent.name } : null),
    [clientId, parent],
  );

  // Ügyfél-kereső a választóhoz (a clients-lista `q`-jával; az első 20 találat).
  const clientSearch = async (q: string): Promise<ComboboxOption[]> => {
    const res = await api.clients.list.query({ query: { q: q || undefined, limit: 20 } });
    return res.status === 200 ? res.body.clients.map((c) => ({ id: c.id, label: c.name })) : [];
  };

  const createMutation = useEntityMutation<AccountFormValues, unknown>({
    mutationFn: (values) => {
      // Guard: a submit-gomb clientId nélkül tiltott, de a hívás-oldalon is védünk (a body clientId kötelező).
      if (!clientId) return Promise.reject(new Error('no-client'));
      return api.accountCreate.mutation({
        body: accountFormToCreateBody(values, clientId),
        headers: { 'idempotency-key': idempotencyKey },
      });
    },
    invalidateKeys: [accountKeys.lists()],
    onFieldErrors: (field, messages) =>
      form.setError(field as keyof AccountFormValues, { message: messages.join(', ') }),
    messages: {
      success: t('toast:accounts.created'),
      conflict: t('toast:accounts.conflict'),
      forbidden: t('toast:accounts.forbidden'),
      validation: t('toast:accounts.validation'),
      error: t('toast:accounts.error'),
    },
  });

  function onSubmit(values: AccountFormValues): void {
    if (!clientId) return;
    createMutation.mutate(values, {
      onSuccess: (data) => {
        const parsed = AccountCreateResponseSchema.safeParse(data.body);
        if (parsed.success) {
          void navigate({ to: '/accounts/$id', params: { id: parsed.data.id } });
        } else {
          void navigate({ to: '/accounts' });
        }
      },
    });
  }

  // Jog-guard (MINDEN hook után — a hook-sorrend stabil marad): accounts.manage nélkül nincs create.
  // Elsődleges védelem a BFF (@RequirePermissions); ez csak a UI-terelés (a lista-gomb amúgy is rejtett).
  if (!permsLoading && !can('accounts.manage')) {
    return (
      <AppShell>
        <FormPage>
          <EmptyState
            title={t('accounts.forbidden')}
            description={t('accounts.forbiddenDesc')}
            action={
              <Button variant="outline" onClick={() => void navigate({ to: '/accounts' })}>
                {t('accounts.back')}
              </Button>
            }
          />
        </FormPage>
      </AppShell>
    );
  }

  return (
    // A cím a TOPBAR morzsájában él („Fiókok / Új fiók") — az oldal törzsében nincs h1, mert duplikálná.
    <AppShell parent={{ label: t('nav.accounts'), to: '/accounts' }} title={t('accounts.create')}>
      <FormPage>
        <AccountForm
          form={form}
          mode="create"
          refs={refs}
          billingProfileOptions={billingProfileOptions}
          clientPicker={{
            value: clientId,
            onChange: (id) => {
              // Új szülő → a régi profil-választás érvénytelen (más ügyfél profiljai) → vissza NONE-ra.
              setClientId(id);
              form.setValue('billingProfileId', ACCOUNT_SELECT_NONE, { shouldDirty: false });
            },
            search: clientSearch,
            initialOption: clientInitialOption,
            locked,
          }}
          pending={createMutation.isPending}
          onSubmit={onSubmit}
          t={t}
          // A Szolgáltatások szekció a mezők KÖZÉ esik (a kért sorrend) — létrehozáskor zárolva.
          servicesSlot={
            <LockedSection
              icon={Square3Stack3DIcon}
              title={t('accounts.sections.services')}
              description={t('accounts.pendingServicesDesc')}
              hint={t('accounts.pendingHint')}
            />
          }
        >
          {/* A két kötés-szekció a mentés-gomb UTÁN, zárolva: kötést csak LÉTEZŐ fiókhoz lehet felvenni. */}
          <LockedSection
            icon={UserGroupIcon}
            title={t('accounts.sections.contacts')}
            description={t('accounts.pendingContactsDesc')}
            hint={t('accounts.pendingHint')}
          />
          <LockedSection
            icon={IdentificationIcon}
            title={t('accounts.sections.admins')}
            description={t('accounts.pendingAdminsDesc')}
            hint={t('accounts.pendingHint')}
          />
        </AccountForm>
      </FormPage>
    </AppShell>
  );
}
